import { useEffect, useRef, useState } from 'react'
import Icon from '../../components/icons.jsx'
import './voice-message.css'

let playingAudio = null
const bars = [12,23,31,18,27,14,8,20,29,17,32,22,12,7,16,25,29,21,27,14,9,18,25,13,8,20,29,15,11,22,30,19,26,12,8,16,24,14,20,10]
const formatTime = seconds => {
  if (!Number.isFinite(seconds) || seconds < 0) return '–:––'
  return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`
}

export default function VoiceMessage({ url, outgoing, avatar, name, pending, error, onOpen, metadata }) {
  const audio = useRef(null)
  const [playing, setPlaying] = useState(false)
  const [busy, setBusy] = useState(false)
  const [duration, setDuration] = useState(0)
  const [position, setPosition] = useState(0)
  const [playError, setPlayError] = useState('')
  const [badAvatar, setBadAvatar] = useState(false)
  const [speed, setSpeed] = useState(1)
  const speedLabel = `${String(speed).replace('.', ',')}×`
  const changeSpeed = () => {
    const next = speed === 1 ? 1.5 : speed === 1.5 ? 2 : 1
    if (audio.current) audio.current.playbackRate = next
    setSpeed(next)
  }
  useEffect(() => { setBadAvatar(false) }, [avatar])
  useEffect(() => {
    const element = audio.current
    return () => { element?.pause(); if (playingAudio === element) playingAudio = null }
  }, [])
  const progress = duration > 0 ? Math.min(100, position / duration * 100) : 0
  const toggle = async () => {
    const element = audio.current
    if (!element || !url) return
    if (!element.paused) { element.pause(); return }
    setPlayError(''); setBusy(true)
    try {
      if (playingAudio && playingAudio !== element) playingAudio.pause()
      playingAudio = element
      await element.play()
    } catch { setPlayError('Não foi possível reproduzir o áudio. Tente novamente ou abra o arquivo.') }
    finally { setBusy(false) }
  }
  return <div className={`at-voice${outgoing ? ' is-outgoing' : ''}`}>
    <audio ref={audio} src={url || undefined} preload="metadata"
      onLoadedMetadata={event => setDuration(Number.isFinite(event.currentTarget.duration) ? event.currentTarget.duration : 0)}
      onDurationChange={event => setDuration(Number.isFinite(event.currentTarget.duration) ? event.currentTarget.duration : 0)}
      onTimeUpdate={event => setPosition(event.currentTarget.currentTime)}
      onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)}
      onError={() => { setPlaying(false); setBusy(false); setPlayError('Não foi possível carregar o áudio. Abra o arquivo para tentar novamente.') }} />
    <button type="button" className="at-voice-avatar" onClick={changeSpeed}
      title={`${name ? `${name} · ` : ''}Velocidade ${speedLabel}. Clique para alterar`}
      aria-label={`Velocidade do áudio: ${speedLabel}. Alterar velocidade`}>
      {avatar && !badAvatar ? <img src={avatar} alt={name ? `Foto de ${name}` : 'Foto de quem enviou'} onError={() => setBadAvatar(true)}/> : <svg className="at-voice-person" viewBox="0 0 48 48" role="img" aria-label={name ? `${name}, sem foto` : 'Pessoa sem foto'}><circle cx="24" cy="17" r="9" fill="currentColor"/><path d="M5 47v-6c0-9 8-14 19-14s19 5 19 14v6Z" fill="currentColor"/></svg>}
      <svg className="at-voice-mic" viewBox="0 0 24 24" aria-hidden="true"><rect x="8" y="1" width="8" height="14" rx="4" fill="currentColor"/><path d="M5 10v2a7 7 0 0 0 14 0v-2M12 19v4M8 23h8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
      <span className={`at-voice-speed${speed !== 1 ? ' is-active' : ''}`} aria-hidden="true">{speedLabel}</span>
    </button>
    <button type="button" className="at-voice-play" onClick={toggle} disabled={!url || busy || pending} aria-label={playing ? 'Pausar áudio' : 'Reproduzir áudio'} title={playing ? 'Pausar áudio' : 'Reproduzir áudio'}>
      {busy || (!url && !error) ? <Icon n="clock" s={23}/> : <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">{playing ? <><rect x="5" y="3" width="5" height="18" rx="1.5"/><rect x="14" y="3" width="5" height="18" rx="1.5"/></> : <path d="M6 3a1 1 0 0 1 1.5-.85l14 8.5a1.5 1.5 0 0 1 0 2.7l-14 8.5A1 1 0 0 1 6 21Z"/>}</svg>}
    </button>
    <div className="at-voice-track">
      <div className="at-voice-wave">
        <svg viewBox="0 0 200 36" preserveAspectRatio="none" aria-hidden="true">{bars.map((height, index) => <rect key={index} x={index * 5} y={(36 - height) / 2} width="2.7" height={height} rx="1.3" fill={index / bars.length * 100 < progress ? '#53bdeb' : '#b9c2bb'}/>)}</svg>
        <input type="range" min="0" max={duration || 1} step="0.1" value={Math.min(position, duration || 1)} disabled={!duration} aria-label="Posição do áudio" aria-valuetext={`${formatTime(position)} de ${formatTime(duration)}`} onChange={event => { const value = Number(event.target.value); audio.current.currentTime = value; setPosition(value) }}/>
        <span className="at-voice-progress" style={{ left:`${progress}%` }} aria-hidden="true"/>
      </div>
      <div className="at-voice-footer">
        <span className="at-voice-time">{pending ? 'Enviando…' : !url && !error ? 'Carregando…' : duration ? formatTime(position > 0 ? position : duration) : '–:––'}</span>
        <button type="button" className="at-voice-open" title="Abrir áudio" aria-label="Abrir áudio" onClick={onOpen} disabled={pending}><Icon n="download" s={13}/></button>
        {metadata}
      </div>
    </div>
    {(error || playError) && <small role="alert" className="at-voice-error">{error || playError}</small>}
  </div>
}
